import { useEffect, useRef, useState } from "react";
import loadingGif from "../../assets/images/Animation-loading-blue-green.gif";
import appInsights from "../../../applicationInsights";

export default function LiveVideoModalGIF({ open, onClose, title, url, language }) {
  const videoRef = useRef(null);
  const [progressPct, setProgressPct] = useState(null);
  const [statusText, setStatusText] = useState("Connecting...");
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [wantsPlay, setWantsPlay] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIsReady(false);
    setIsLoading(true);
    setWantsPlay(false);
    setProgressPct(null);
    setStatusText("Connecting...");

    queueMicrotask(() => {
      try {
        videoRef.current?.load();
      } catch {}
    });
  }, [open, url]);

  const handleCanPlay = () => {
    setIsReady(true);
    setIsLoading(false);
    setStatusText("Ready");
  };

  const handleWaiting = () => {
    setIsLoading(true);
    setStatusText("Buffering...");
  };

  const handlePlaying = () => {
    setIsReady(true);
    setIsLoading(false);
    setStatusText("Playing");

    appInsights.trackEvent({
      name: "VideoPlayed",
      properties: {
        language,
        title,
        url,
      },
    });
  };

  const handleProgress = (e) => {
    const v = e.currentTarget;
    if (!v?.duration || !Number.isFinite(v.duration)) return;
    if (!v.buffered || v.buffered.length === 0) return;

    const end = v.buffered.end(v.buffered.length - 1);
    const pct = Math.max(0, Math.min(100, (end / v.duration) * 100));
    setProgressPct(pct);
    if (!isReady) setStatusText("Loading...");
  };

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    onClose();
  };

  if (!open) return null;

  return (
    <div className="video-modal-overlay" onClick={handleClose}>
      <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            padding: "10px 0",
            display: "flex",
            alignItems: "center",
            gap: 10
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {title}
            </div>

            <div
              style={{
                fontSize: 12,
                padding: "6px 10px",
                borderRadius: 999,
                background: "rgba(0,0,0,0.35)",
                color: "#fff",
                whiteSpace: "nowrap"
              }}
            >
              {isReady ? "Ready" : progressPct !== null ? `Loading ${Math.round(progressPct)}%` : statusText}
            </div>
          </div>

          <button
            onClick={handleClose}
            style={{
              marginLeft: "auto",
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "none",
              background: "rgba(0,0,0,0.25)",
              color: "#fff",
              fontSize: 20,
              cursor: "pointer"
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={{ position: "relative" }}>
          <video
            ref={videoRef}
            src={url}
            controls={isReady}
            playsInline
            preload="auto"
            controlsList="nodownload"
            onProgress={handleProgress}
            onCanPlay={handleCanPlay}
            onLoadedData={handleCanPlay}
            onWaiting={handleWaiting}
            onPlaying={handlePlaying}
            style={{
              width: "100%",
              maxHeight: "70vh",
              borderRadius: 12,
              opacity: isReady ? 1 : 0,
              pointerEvents: isReady ? "auto" : "none",
              position: isReady ? "relative" : "absolute",
              inset: isReady ? "auto" : 0
            }}
          />

          {!isReady && (
            <img
              src={loadingGif}
              alt="Loading…"
              style={{
                width: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: 12,
                display: "block"
              }}
            />
          )}

          {isReady && !wantsPlay && (
            <button
              onClick={() => {
                setWantsPlay(true);
                videoRef.current?.play().catch(() => {});
              }}
              style={{
                position: "absolute",
                inset: 0,
                margin: "auto",
                width: 90,
                height: 90,
                borderRadius: 999,
                border: "none",
                cursor: "pointer",
                fontSize: 28,
                fontWeight: 700,
                background: "rgba(0,0,0,0.45)",
                color: "white"
              }}
            >
              ▶
            </button>
          )}

          {isReady && isLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
                background: "rgba(0,0,0,0.15)",
                borderRadius: 12
              }}
            >
              <img src={loadingGif} alt="Buffering…" style={{ width: 120 }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}